import { CommonModule } from '@angular/common';
import { Component, Input, OnChanges, OnDestroy, SimpleChanges } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { NgSelectModule } from '@ng-select/ng-select';
import { HorizonTrackingService } from '@services/api/horizon-tracking.service';
import { shouldHideError } from '@services/helpers/server-error-silence';
import { ToasterService } from '@services/util/toaster.service';
import { apiErrorBody } from '@services/api/api-error';
import moment from 'moment';
import { SharedModule } from 'primeng/api';
import { DialogModule } from 'primeng/dialog';
import { InputNumberModule } from 'primeng/inputnumber';
import { Subscription } from 'rxjs';
import {
  HorizonCountOption,
  HorizonPreferenceOptions,
  HorizonPreferences,
  HorizonPreferencesResponse,
  HorizonShortlistItem,
} from '../lead-horizon-tracking.model';

const BRISBANE_UTC_OFFSET_MINUTES = 600;
const REQUIREMENTS_LIMIT = 500;

/** Used only when the project sends no choices for a count, so the picker is never empty. */
const FALLBACK_COUNTS = [1, 2, 3, 4, 5];
const FALLBACK_CAR_SPACES = [0, 1, 2, 3];

/** `850,000.00` — money is always shown with thousands separators and cents. */
const MONEY_FORMAT: Intl.NumberFormatOptions = { minimumFractionDigits: 2, maximumFractionDigits: 2 };

type CountChoice = { value: number; label: string };

/** Offered when the project's lots carry no property type to check against. */
const DEFAULT_PROPERTY_TYPES = [
  { key: 'apartment', label: 'Apartment' },
  { key: 'townhouse', label: 'Townhouse' },
  { key: 'penthouse', label: 'Penthouse' },
  { key: 'terrace', label: 'Terrace' },
  { key: 'villa', label: 'Villa' },
];

/** With no counts to check against, any whole number up to this is accepted. */
const FREE_COUNT_MAX = 10;

/** The backend sends `{ value, label }` choices; older responses sent plain numbers. Both become `{ value, label }`. */
function toChoices(items: (HorizonCountOption | number)[] | null | undefined, fallback: number[]): CountChoice[] {
  const choices = (items ?? [])
    .map(item =>
      typeof item === 'number' ? { value: item, label: String(item) } : { value: Number(item?.value), label: item?.label ?? String(item?.value) }
    )
    .filter(choice => Number.isFinite(choice.value));
  return choices.length ? choices : fallback.map(value => ({ value, label: String(value) }));
}

const EMPTY_PREFERENCES: HorizonPreferences = {
  budget_min: null,
  budget_max: null,
  property_type: null,
  bedrooms: null,
  bathrooms: null,
  car_spaces: null,
  features: [],
  additional_requirements: null,
};

/**
 * Buyer Preferences on the lead's Horizon tab.
 *
 * What this buyer saved for the project in Horizon, and their shortlist. It is the same record the
 * portal edits, so an agent's edit shows up for the buyer and the other way round. Nothing is
 * shown, or assumed, when the buyer has saved nothing.
 */
@Component({
  selector: 'app-horizon-buyer-preferences',
  standalone: true,
  imports: [CommonModule, FormsModule, NgSelectModule, SharedModule, DialogModule, InputNumberModule],
  templateUrl: './horizon-buyer-preferences.component.html',
  styleUrls: ['../horizon-card.scss', './horizon-buyer-preferences.component.scss'],
})
export class HorizonBuyerPreferencesComponent implements OnChanges, OnDestroy {
  @Input() leadId: string | null = null;
  @Input() projectId: string | null = null;
  /** Replaced by the tab on every reload, so the shortlist is re-read with the rest of the tab instead of only when the lead changes. */
  @Input() range: { from?: string; to?: string } = {};

  readonly requirementsLimit = REQUIREMENTS_LIMIT;

  data: HorizonPreferencesResponse | null = null;
  shortlist: HorizonShortlistItem[] = [];
  options: HorizonPreferenceOptions | null = null;
  isLoading = false;
  hasLoadError = false;

  isDialogOpen = false;
  isSaving = false;
  readonly freeCountMax = FREE_COUNT_MAX;
  /** The backend's message for each field it refused, keyed by field name, shown under that field. */
  fieldErrors: Record<string, string> = {};
  propertyTypes: { key: string; label: string }[] = DEFAULT_PROPERTY_TYPES;
  draft: HorizonPreferences = { ...EMPTY_PREFERENCES };
  /** Choices for the number pickers, built once when the options arrive. */
  bedroomChoices: CountChoice[] = toChoices(null, FALLBACK_COUNTS);
  bathroomChoices: CountChoice[] = toChoices(null, FALLBACK_COUNTS);
  carChoices: CountChoice[] = toChoices(null, FALLBACK_CAR_SPACES);

  private subscriptions: Subscription[] = [];

  constructor(
    private horizon: HorizonTrackingService,
    private toaster: ToasterService
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if ((changes['leadId'] || changes['projectId'] || changes['range']) && this.leadId) {
      this.load();
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(subscription => subscription.unsubscribe());
  }

  load(): void {
    this.subscriptions.forEach(subscription => subscription.unsubscribe());
    this.isLoading = true;
    this.hasLoadError = false;
    this.subscriptions = [
      this.horizon.getLeadPreferences(this.leadId).subscribe({
        next: res => {
          this.data = res?.data ?? null;
          this.isLoading = false;
        },
        error: () => {
          this.hasLoadError = !shouldHideError(null);
          this.isLoading = false;
        },
      }),
      this.horizon.getLeadShortlist(this.leadId, this.projectId).subscribe({
        next: res => (this.shortlist = res?.data?.available === false ? [] : (res?.data?.items ?? [])),
        error: () => (this.shortlist = []),
      }),
    ];
  }

  // ---- Reading ---------------------------------------------------------------------------------

  /** A project whose lots carry no count for a field has nothing to offer, so that field becomes a plain number input. */
  get bedroomsFree(): boolean {
    return !!this.options && !this.options.bedrooms?.length;
  }

  get bathroomsFree(): boolean {
    return !!this.options && !this.options.bathrooms?.length;
  }

  get carSpacesFree(): boolean {
    return !!this.options && !this.options.car_spaces?.length;
  }

  get preferences(): HorizonPreferences | null {
    return this.data?.preferences ?? null;
  }

  get hasPreferences(): boolean {
    return !!this.preferences;
  }

  get budgetText(): string {
    const { budget_min, budget_max } = this.preferences ?? EMPTY_PREFERENCES;
    if (budget_min && budget_max) {
      return `${this.money(budget_min)} – ${this.money(budget_max)}`;
    }
    if (budget_max) {
      return `Up to ${this.money(budget_max)}`;
    }
    return budget_min ? `From ${this.money(budget_min)}` : '—';
  }

  money(value: number): string {
    return `$${(value ?? 0).toLocaleString('en-AU', MONEY_FORMAT)}`;
  }

  featureLabel(key: string): string {
    const known = this.data?.feature_labels?.find(feature => feature.key === key) ?? this.options?.features.find(feature => feature.key === key);
    return known?.label ?? key.replace(/_/g, ' ').replace(/^./, char => char.toUpperCase());
  }

  /** Bedrooms, bathrooms and car spaces are minimums, so 2 reads "2+". */
  minimum(value: number | null): string {
    return value || value === 0 ? `${value}+` : '—';
  }

  typeLabel(key: string | null): string {
    return this.propertyTypes.find(type => type.key === key)?.label ?? (key ? key.replace(/^./, char => char.toUpperCase()) : '—');
  }

  get updatedText(): string {
    if (!this.data?.updated_at) {
      return '';
    }
    const when = moment(this.data.updated_at).utcOffset(BRISBANE_UTC_OFFSET_MINUTES).format('D MMM YYYY');
    return `Saved ${this.data.updated_by?.type === 'agent' ? 'by an agent' : 'by the buyer'} on ${when}`;
  }

  // ---- Editing -----------------------------------------------------------------------------------

  openEdit(): void {
    this.draft = { ...EMPTY_PREFERENCES, ...(this.preferences ?? {}), features: [...(this.preferences?.features ?? [])] };
    this.fieldErrors = {};
    this.isDialogOpen = true;
    if (!this.options) {
      this.loadOptions();
    }
  }

  private loadOptions(): void {
    this.subscriptions.push(
      this.horizon.getPreferenceOptions(this.projectId).subscribe({
        next: res => {
          this.options = res?.data ?? null;
          this.propertyTypes = this.options?.property_types?.length ? this.options.property_types : DEFAULT_PROPERTY_TYPES;
          this.bedroomChoices = toChoices(this.options?.bedrooms, FALLBACK_COUNTS);
          this.bathroomChoices = toChoices(this.options?.bathrooms, FALLBACK_COUNTS);
          this.carChoices = toChoices(this.options?.car_spaces, FALLBACK_CAR_SPACES);
        },
        error: () => this.toaster.Error('The preference options could not be loaded.'),
      })
    );
  }

  // ---- Budget inputs ------------------------------------------------------------------------------
  // The budget fields are PrimeNG InputNumbers bound straight to `draft`: they show "850,000" with
  // separators while typing and on blur, and the model (and so the API payload) stays a plain number.

  /** "Between $950,000.00 and $2,400,000.00", from the project's lots. */
  get budgetHint(): string {
    const budget = this.options?.budget;
    return budget ? `This project's prices run from ${this.money(budget.min)} to ${this.money(budget.max)}.` : '';
  }

  get isRangeInvalid(): boolean {
    const { budget_min, budget_max } = this.draft;
    return !!budget_min && !!budget_max && budget_min > budget_max;
  }

  /** Only plain values go to the backend: numbers as numbers, blanks as null. */
  private get payload(): HorizonPreferences {
    const count = (value: unknown): number | null => {
      const raw = value && typeof value === 'object' ? (value as CountChoice).value : value;
      return raw === null || raw === undefined || raw === '' ? null : Number(raw);
    };
    const draft = this.draft;
    return {
      budget_min: draft.budget_min ?? null,
      budget_max: draft.budget_max ?? null,
      property_type: draft.property_type || null,
      bedrooms: count(draft.bedrooms),
      bathrooms: count(draft.bathrooms),
      car_spaces: count(draft.car_spaces),
      features: [...(draft.features ?? [])],
      additional_requirements: draft.additional_requirements?.trim() || null,
    };
  }

  save(): void {
    if (this.isSaving || this.isRangeInvalid) {
      return;
    }
    this.isSaving = true;
    this.fieldErrors = {};
    this.horizon.saveLeadPreferences(this.leadId, this.payload).subscribe({
      next: res => {
        this.isSaving = false;
        this.isDialogOpen = false;
        this.toaster.Success(
          /^(ok|success)\.?$/i.test(res?.message ?? '') || !res?.message
            ? 'Preferences saved. The buyer will see the change in Horizon.'
            : res.message
        );
        this.load();
      },
      error: err => {
        this.isSaving = false;
        // `field_errors` may sit on the body or under `data`, and each value may be a string or a list.
        const fields = apiErrorBody(err).field_errors ?? apiErrorBody(err).data?.field_errors;
        this.fieldErrors = Object.fromEntries(
          Object.entries(fields && typeof fields === 'object' ? fields : {}).map(([field, value]) => [
            field,
            (Array.isArray(value) ? value : [value]).map(item => String(item)).join(' '),
          ])
        );
        this.toaster.Error(apiErrorBody(err).message || 'The preferences could not be saved.');
      },
    });
  }

  trackByUnit(_index: number, item: HorizonShortlistItem): string {
    return item.unit_id;
  }
}
